import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaChevronDown, FaGlobe } from 'react-icons/fa';
import { useI18n } from '../i18n/I18nContext';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import { getLocalizedPath, getLocalizedRoute } from '../i18n/localizedRoutes';

const LanguageMenu = ({ location }) => {
  const { locale, localeDefinition, locales, setLocale, t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const requestedFocusRef = useRef(null);
  const hasLanguageUrls = location && getLocalizedRoute(location.pathname);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !requestedFocusRef.current) return;

    const options = menuRef.current?.querySelectorAll('[role="menuitemradio"]');
    const index = requestedFocusRef.current === 'last' ? options?.length - 1 : 0;
    options?.[index]?.focus();
    requestedFocusRef.current = null;
  }, [isOpen]);

  const selectLocale = (nextLocale) => {
    setLocale(nextLocale);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === ' ' && event.target.tagName === 'A') {
      event.preventDefault();
      event.target.click();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const options = [...event.currentTarget.querySelectorAll('[role="menuitemradio"]')];
    const currentIndex = options.indexOf(document.activeElement);
    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1 + options.length) % options.length;
    if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
    options[nextIndex]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    requestedFocusRef.current = event.key === 'ArrowUp' ? 'last' : 'first';
    setIsOpen(true);
  };

  return (
    <div className="language-selector" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="language-trigger"
        aria-label={t('language.current', { language: localeDefinition.label })}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={handleTriggerKeyDown}
      >
        <FaGlobe aria-hidden="true" />
        <span>{localeDefinition.shortLabel}</span>
        <FaChevronDown className="language-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="language-menu"
          ref={menuRef}
          role="menu"
          aria-label={t('language.select')}
          onKeyDown={handleMenuKeyDown}
        >
          <div className="language-menu-title" role="presentation">{t('language.select')}</div>
          {locales.map((option) => {
            const Option = hasLanguageUrls ? Link : 'button';
            return (
            <Option
              key={option.code}
              {...(hasLanguageUrls ? { to: getLocalizedPath(`${location.pathname}${location.search}${location.hash}`, option.code) } : { type: 'button' })}
              role="menuitemradio"
              aria-checked={locale === option.code}
              className="language-option"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                selectLocale(option.code);
              }}
            >
              <span lang={option.code}>{option.label}</span>
              {locale === option.code && <FaCheck aria-hidden="true" />}
            </Option>
          );})}
        </div>
      )}
    </div>
  );
};

const RoutedLanguageSelector = () => <LanguageMenu location={useLocation()} />;
const LanguageSelector = () => useInRouterContext() ? <RoutedLanguageSelector /> : <LanguageMenu />;

export default LanguageSelector;
